/**
 * @puffin-core/route-match (CJS entry)
 *
 * Canonical, tab-aware route-pattern matcher shared by the ParcelPuffin
 * onboarding platform's backend (Node — coach-mark / empty-state route
 * resolution) and frontend (browser — onboarding-ui sub-item matching).
 *
 * Promoted from `@puffin-core/onboarding-ui`'s `checklistHelpers.js`
 * (`subItemMatchScore`) so backend and frontend agree on ONE matcher.
 * Replaces the exact-string-equality bug in
 * `OnboardingCoachMarkDefinition.listPublishedForRoute` that left every
 * `?tab=…` settings coach mark dark: a mark seeded with the bare
 * `routePattern:'/admin/settings'` never matched `/admin/settings?tab=sales`.
 *
 * Zero dependencies. The ESM parallel copy lives at `lib/esm/index.js`;
 * the two are kept byte-identical in logic (dual-package convention).
 *
 * @module @puffin-core/route-match
 */

'use strict';

/**
 * Strip the query string + hash from a URL-like string. The "page-level"
 * prefix used to decide whether two routes point at the same page at all.
 *
 * @param {string} value
 * @returns {string}
 */
function basePath(value) {
  if (typeof value !== 'string' || value.length === 0) return '';
  return value.split('?')[0].split('#')[0];
}

/**
 * Split a URL-like string into `{ pathname, query }`. The query portion
 * preserves the leading `?` and all params; the pathname has both the
 * query and the fragment removed.
 *
 * @param {string} value
 * @returns {{pathname: string, query: string}}
 */
function splitPathAndQuery(value) {
  if (typeof value !== 'string' || value.length === 0) {
    return { pathname: '', query: '' };
  }
  const hashStripped = value.split('#')[0];
  const qIdx = hashStripped.indexOf('?');
  if (qIdx === -1) {
    return { pathname: hashStripped, query: '' };
  }
  return { pathname: hashStripped.slice(0, qIdx), query: hashStripped.slice(qIdx) };
}

/**
 * Whether `routePath` is the same page as `patternPath` or a deeper sub-route
 * of it, comparing whole PATH SEGMENTS.
 *
 * This is deliberately not a raw `startsWith`. A raw prefix also matches a
 * sibling whose name merely begins with the pattern — `/schedule` would claim
 * `/schedule-archive`, `/sales-plan` would claim `/sales-plans` — because the
 * boundary between "deeper sub-route" and "different page that shares a naming
 * stem" is the `/`, not the character count. (Fixed 2026-07-26. No seeded
 * pattern collided at the time — all 40 were audited — so this closes a latent
 * hazard rather than a live defect; the specificity tie-break in
 * `matchRoutePattern` had been masking it for the one real overlap,
 * `/schedule` vs `/schedule/templates/create`.)
 *
 * A root pattern (`'/'`) still matches every path, and an empty pattern path
 * (a degenerate query-only pattern) still constrains nothing — both preserve
 * the prior behaviour exactly.
 *
 * @param {string} routePath - pathname only, query/hash already stripped
 * @param {string} patternPath - pathname only, query/hash already stripped
 * @returns {boolean}
 */
function isSameOrDeeperPath(routePath, patternPath) {
  if (patternPath === '') return true;
  if (routePath === patternPath) return true;
  const withBoundary = patternPath.endsWith('/') ? patternPath : `${patternPath}/`;
  return routePath.startsWith(withBoundary);
}

/**
 * Score a route `pattern` against a candidate `route`. Returns 0 (no
 * match) OR a positive number indicating specificity (higher = more
 * specific). Consumers that need a boolean test `score > 0`; consumers
 * that pick a single best candidate (empty states) take the max score.
 *
 * Matching semantics:
 *
 *   - **Empty / non-string pattern or route**: 0.
 *   - **Pathname mismatch**: 0. `route`'s pathname must be the pattern's
 *     pathname or a deeper sub-route of it, compared by whole SEGMENTS —
 *     see `isSameOrDeeperPath`.
 *   - **Bare pattern (no query)**: matches any query variant on the same
 *     page. Score = `pathname.length` (the "sibling-tab fallback"): a
 *     default-tab mark still claims tab variants that no explicit-tab
 *     pattern owns, but loses tie-breaks to explicit-tab patterns.
 *   - **Query-bearing pattern**: matches only when `route`'s query equals
 *     the pattern's query OR starts with it followed by `&` (extra route
 *     params are fine; differing values are not). Score = full
 *     `pattern.length`, necessarily longer than the bare case, so
 *     explicit-tab patterns win the tie-break.
 *   - **Query-bearing pattern vs a bare or sibling route query**: 0.
 *
 * @param {string} pattern - The seeded `routePattern` (e.g. `/admin/settings`
 *   or `/admin/settings?tab=holidays`).
 * @param {string} route - The visitor's current `pathname + search`.
 * @returns {number} 0 for no-match; > 0 indicates specificity (higher wins).
 */
function matchRoutePattern(pattern, route) {
  if (typeof pattern !== 'string' || pattern.length === 0) return 0;
  if (typeof route !== 'string' || route.length === 0) return 0;

  const { pathname: patternPath, query: patternQuery } = splitPathAndQuery(pattern);
  const { pathname: routePath, query: routeQuery } = splitPathAndQuery(route);

  // Pathname must agree at the SEGMENT level — same page, or a deeper
  // sub-route of it. A pattern never matches a different page just because
  // its query happens to overlap, nor a sibling that merely shares a naming
  // stem (`/schedule` must not claim `/schedule-archive`).
  if (!isSameOrDeeperPath(routePath, patternPath)) return 0;

  if (patternQuery === '') {
    // Bare pattern — matches any query variant on the same page.
    // Specificity = pathname length so it loses tie-breaks to
    // explicit-tab patterns on the same page.
    return patternPath.length;
  }

  if (routeQuery === '') {
    // Pattern names a specific tab, but the route is bare (default tab).
    // A query-bearing pattern must NOT claim the bare route.
    return 0;
  }

  // Both sides carry a query. Match exactly OR the pattern's query is a
  // strict `&`-separated prefix of the route's query.
  if (routeQuery === patternQuery) return pattern.length;
  if (routeQuery.startsWith(`${patternQuery}&`)) return pattern.length;
  return 0;
}

/**
 * Did a BARE pattern claim `route` only through the sibling-tab fallback?
 *
 * `matchRoutePattern` lets a pattern with no query claim every query variant of
 * its page — the "default-tab mark still claims tab variants" rule above. That is
 * the right DECISION, but a consumer anchored to one specific tab cannot act on it
 * anywhere but that tab: the coach-mark sequence's anchor-existence gate renders
 * null there, and its `anchor_missing` telemetry used to record every such visit
 * as a reachability break (three rows in one four-minute browser run,
 * PLN-DEPT-GRAIN-001 L7 Q-3). This names the case so a consumer can tell "claimed
 * onto a sibling tab" from "genuinely dangling anchor".
 *
 * True exactly when the pattern carries no query, the route does, and the pair
 * matches. A query-bearing pattern never falls back, so it is never true for one;
 * a bare route (the default tab) is the bare pattern's OWN page, never a fallback.
 *
 * @param {string} pattern - The seeded `routePattern`.
 * @param {string} route - The visitor's current `pathname + search`.
 * @returns {boolean}
 */
function matchedBySiblingTabFallback(pattern, route) {
  if (matchRoutePattern(pattern, route) === 0) return false;
  return splitPathAndQuery(pattern).query === '' && splitPathAndQuery(route).query !== '';
}

/**
 * Build a route with appended URL search parameters — the INVERSE of
 * `splitPathAndQuery`, and the ONE place deep-link query encoding lives.
 *
 * NUDGE-LINK-001 (2026-09-06): promoted here from the planner-web and
 * planner-api route registries, which each carried their own copy — and
 * the two had DIVERGED (the api copy stringified `null`/`undefined` into
 * `?tab=null`). Every app's registry consumer and every onboarding seed
 * now builds a `?tab=` / `?scope=` / `?action=` deep link through this
 * function, so a link's shape can be checked in one place by the
 * SETTINGS-LINK-001 contract.
 *
 * Encoding is `URLSearchParams` (what the browser sends for a form GET:
 * spaces become `+`, reserved chars are percent-encoded). `undefined` and
 * `null` values are OMITTED — an absent tab is the bare page, never the
 * literal string "undefined". A path that is not a non-empty string is a
 * programming error and throws, so a registry typo cannot reach the DOM
 * as `undefined?tab=labor`.
 *
 * @param {string} path - A registry value (e.g. `PLANNER_ROUTES.ADMIN_SETTINGS`).
 * @param {Object<string, string|number|boolean|null|undefined>} [params] - Query map.
 * @returns {string} The path, with `?key=value&...` appended when any param is set.
 * @throws {TypeError} when `path` is not a non-empty string.
 *
 * @example
 *   withQuery('/admin/settings', { tab: 'labor' })   // → '/admin/settings?tab=labor'
 *   withQuery('/admin/settings', { tab: undefined }) // → '/admin/settings'
 *   withQuery('/admin/settings')                     // → '/admin/settings'
 */
function withQuery(path, params) {
  if (typeof path !== 'string' || path.length === 0) {
    throw new TypeError(
      `withQuery: path must be a non-empty string (received ${JSON.stringify(path)})`,
    );
  }
  if (!params) return path;
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Append a fragment id to an href — the ONE way a link gains its anchor
 * (PLN-DEPT-UX-005 B-R). Composes over `withQuery`: the query first, the
 * fragment last, which is the only order a URL admits.
 *
 * The two-ended anchor convention (a `PLANNER_ANCHORS` constant pinned on a
 * `<PillSection labelId>`, honoured by `useHashAnchorScroll`) had no builder:
 * `laborBudgetVsActualPath` concatenated `#` by hand. A second spelling of
 * "href plus anchor" is the drift this exists to end.
 *
 * @param {string} href - The path, with or without a query string.
 * @param {string} anchor - A bare fragment id (`history-exclusions`); ONE leading
 *   `#` is tolerated so a constant may be spelled either way.
 * @returns {string} `${href}#${anchor}`
 * @throws {TypeError} when `href` or `anchor` is not a non-empty string, when the
 *   anchor is not a bare fragment id (a `#`, a `?` or whitespace inside it), or
 *   when the href already carries a fragment — two anchors is a bug, not a merge.
 *
 * @example
 *   withHash(withQuery('/admin/settings', { tab: 'departments' }), 'history-exclusions')
 *   // → '/admin/settings?tab=departments#history-exclusions'
 */
function withHash(href, anchor) {
  if (typeof href !== 'string' || href.length === 0) {
    throw new TypeError(
      `withHash: href must be a non-empty string (received ${JSON.stringify(href)})`,
    );
  }
  if (typeof anchor !== 'string' || anchor.length === 0) {
    throw new TypeError(
      `withHash: anchor must be a non-empty string (received ${JSON.stringify(anchor)})`,
    );
  }
  const id = anchor.startsWith('#') ? anchor.slice(1) : anchor;
  if (id.length === 0) {
    throw new TypeError('withHash: anchor must be a non-empty string (received "#")');
  }
  if (id.includes('#')) {
    throw new TypeError(`withHash: an anchor is one fragment id, never two (received "${anchor}")`);
  }
  if (id.includes('?')) {
    throw new TypeError(
      `withHash: an anchor carries no query — build the query with withQuery first (received "${anchor}")`,
    );
  }
  if (/\s/.test(id)) {
    throw new TypeError(`withHash: an anchor carries no whitespace (received "${anchor}")`);
  }
  if (href.includes('#')) {
    throw new TypeError(`withHash: href already carries a fragment (received "${href}")`);
  }
  return `${href}#${id}`;
}

/**
 * Fill a parametrised route pattern (`/labor-budgets/:identifier`) with its
 * params.
 *
 * ROUTE-CONCAT-001 (2026-09-07): this is the ONE way a `:param` destination is
 * built. It sits beside `withQuery` because they are the same job at two grains —
 * a registry value in, a real URL out — and because the alternative is what this
 * replaced: every app hand-building `/x/${id}/edit` at the callsite, which no
 * route-registry contract can see. Two live 404s shipped that way.
 *
 * Promoted from planner-web's `plannerRoutePaths.js`, which had the only copy;
 * the other nine registries had none, which is why they had no param constants
 * to build from either.
 *
 * A missing value THROWS rather than emitting `/x/undefined` or a literal
 * `/x/:identifier`. Both of those are silent 404s — the exact failure mode this
 * standard exists to end — and a route built from an absent id is a programming
 * error, not a user-facing state.
 *
 * @param {string} pattern - A registry value with `:name` segments
 * @param {Object<string, string|number>} params - Values keyed by segment name
 * @returns {string} The filled path, each value URI-encoded
 * @throws {Error} when any `:name` in the pattern has no value
 */
function fillRoute(pattern, params) {
  // ⚠️ `[A-Za-z]+` was WRONG: React Router param names may carry digits and
  // underscores, and `:customerE164` parsed as `:customerE` — leaving `164`
  // stranded as a literal and throwing `missing "customerE"`. Found by port-web's
  // `/sms/unified/:customerE164` during ROUTE-PARAM-REGISTRIES-001 (2026-09-08).
  // A name starts with a letter or underscore, then any word character.
  return pattern.replace(/:([A-Za-z_]\w*)/g, (segment, name) => {
    const value = params?.[name];
    if (value === undefined || value === null || value === '') {
      throw new Error(`fillRoute: missing "${name}" for ${pattern}`);
    }
    return encodeURIComponent(String(value));
  });
}

/**
 * The anchor props of an IN-APP link that is also a real link (PALM-PHONE-UAT-001 A10): a page
 * header's navigation verb renders as an anchor styled as its button (`href`, so middle-click, a
 * modifier-click and the link role keep working), and a plain left click goes through the
 * router instead of a document load — what react-router's `<Link>` does, for a descriptor that
 * cannot hold a component. A click with a modifier key, a non-primary button, or one a handler
 * already prevented is left to the browser.
 *
 * The handler reads the anchor's click EVENT (it is never called without one): its
 * `defaultPrevented`, its `button` and its four modifier keys decide whether the click is the
 * router's; when it is, `preventDefault()` stops the document load.
 *
 * @param {string} href - The app-relative path
 * @param {(to: string) => void} navigate - The router's `navigate`
 * @returns {{ href: string, onClick: (event: { defaultPrevented: boolean, button: number,
 *   metaKey: boolean, ctrlKey: boolean, shiftKey: boolean, altKey: boolean,
 *   preventDefault: () => void }) => void }}
 */
function appLinkProps(href, navigate) {
  return {
    href,
    onClick(event) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      navigate(href);
    },
  };
}

module.exports = {
  appLinkProps,
  basePath,
  fillRoute,
  splitPathAndQuery,
  matchRoutePattern,
  matchedBySiblingTabFallback,
  withHash,
  withQuery,
};
